import React, { useEffect } from "react";
import { Navigate, Outlet, useMatch, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import AppShell from "../components/layout/AppShell";
import AuditDashboard from "../components/audit/AuditDashboard.jsx";
import { InboxAuditPromoEmptyState } from "../components/audit/InboxAuditPromoEmptyState.jsx";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import { PageFirstLoadSpinner } from "../components/ui/PageFirstLoadSpinner.jsx";
import { useMailboxScope } from "../contexts/MailboxScopeContext";
import { leadsService } from "../services/leads";

/** API list is ordered by -create_date. */
function latestBulkTaskForMailbox(tasks, mailboxHash) {
  if (!mailboxHash || !Array.isArray(tasks)) return null;
  return tasks.find((t) => t.mailbox_hash === mailboxHash) ?? null;
}

function runningBulkTaskHashForMailbox(tasks, mailboxHash) {
  const latest = latestBulkTaskForMailbox(tasks, mailboxHash);
  if (!latest?.hash || latest.status !== "running") return null;
  return latest.hash;
}

function mailboxHasAuditResults(data) {
  const examined = data?.kpis?.examined_inbounds ?? 0;
  const coldPitch = data?.kpis?.cold_pitch_inbounds ?? 0;
  return examined > 0 || coldPitch > 0;
}

const Audit = ({ currentUser }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mailboxScope = useMailboxScope();
  const legacyTaskParam = (searchParams.get("task") || "").trim();
  const openRunning = searchParams.get("openRunning") === "1";
  const auditIndexMatch = useMatch({ path: "/audit/", end: true });

  const scopeBooting = Boolean(mailboxScope?.mailboxesLoading);
  const mailboxReady = Boolean(mailboxScope?.mailboxHash) && !scopeBooting;

  const mailboxAuditQuery = useQuery({
    queryKey: ["mailbox-audit-results", mailboxScope?.mailboxHash, "all"],
    queryFn: () => leadsService.getMailboxAuditResults(mailboxScope.mailboxHash, "all"),
    enabled: mailboxReady,
  });

  const bulkTasksQuery = useQuery({
    queryKey: ["bulk-email-tasks", mailboxScope?.mailboxHash],
    queryFn: () => leadsService.listBulkEmailTasks(),
    enabled: mailboxReady && openRunning,
  });

  const hasResults =
    mailboxAuditQuery.isSuccess && mailboxHasAuditResults(mailboxAuditQuery.data);

  useEffect(() => {
    if (!openRunning) return;
    if (!auditIndexMatch) return;
    if (!mailboxReady) return;
    if (!bulkTasksQuery.isSuccess) return;
    const h = runningBulkTaskHashForMailbox(bulkTasksQuery.data, mailboxScope?.mailboxHash);
    if (!h) return;
    navigate(`/audit/new/${h}/`, { replace: true });
  }, [
    openRunning,
    auditIndexMatch,
    bulkTasksQuery.data,
    bulkTasksQuery.isSuccess,
    mailboxReady,
    mailboxScope?.mailboxHash,
    navigate,
  ]);

  const showAuditBodySpinner = scopeBooting || (mailboxReady && mailboxAuditQuery.isLoading);
  const showMailboxInsightsError = mailboxReady && mailboxAuditQuery.isError;
  const showMailboxPromo = mailboxReady && mailboxAuditQuery.isSuccess && !hasResults;
  const showMailboxDashboard = mailboxReady && mailboxAuditQuery.isSuccess && hasResults;

  if (legacyTaskParam) {
    return <Navigate to="/audit/" replace />;
  }

  return (
    <AppShell currentUser={currentUser} pageTitle="Inbox audit" pageSubtitle="Who is emailing you, and who is a great fit">
      <div className="space-y-6">
        <div className="hidden md:flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display font-bold italic text-[40px] leading-[1.1] text-theme-on-background">
              Inbox audit
            </h1>
            <p className="font-mono text-[12px] text-theme-dim mt-1 uppercase tracking-[0.08em]">
              Who is emailing you, who fits your ICP, and what the inbox reveals
            </p>
          </div>
          {showMailboxDashboard ? (
            <div className="flex flex-wrap items-center gap-3 justify-end">
              <Button type="button" variant="secondary" onClick={() => navigate("/audit/past/")}>
                Past audits
              </Button>
              <Button type="button" onClick={() => navigate("/audit/new/")}>
                New inbox audit
              </Button>
            </div>
          ) : null}
        </div>

        <div className="flex md:hidden flex-col gap-2">
          <h1 className="font-display font-bold italic text-[28px] leading-[1.1] text-theme-on-background">
            Inbox audit
          </h1>
          <p className="font-mono text-[10px] text-theme-dim uppercase tracking-[0.08em]">
            Mailbox intelligence and matched leads
          </p>
          {showMailboxDashboard ? (
            <div className="flex flex-wrap gap-2 pt-1 w-full">
              <Button type="button" variant="secondary" size="sm" onClick={() => navigate("/audit/past/")}>
                Past audits
              </Button>
              <Button type="button" size="sm" onClick={() => navigate("/audit/new/")}>
                New inbox audit
              </Button>
            </div>
          ) : null}
        </div>

        {!mailboxScope?.mailboxHash && !scopeBooting ? (
          <Card className="p-8 text-center">
            <p className="text-theme-text">Connect a mailbox in Settings to run an audit.</p>
          </Card>
        ) : null}

        {showAuditBodySpinner ? <PageFirstLoadSpinner /> : null}

        {showMailboxInsightsError ? (
          <Card className="p-8 text-center border border-theme-border">
            <p className="text-theme-muted text-sm">Could not load mailbox audit data. Try again in a moment.</p>
          </Card>
        ) : null}

        {showMailboxPromo ? <InboxAuditPromoEmptyState /> : null}

        {showMailboxDashboard ? (
          <AuditDashboard
            mailboxHash={mailboxScope.mailboxHash}
            mailboxAudit={{
              data: mailboxAuditQuery.data,
              isLoading: mailboxAuditQuery.isLoading && !mailboxAuditQuery.data,
              isError: mailboxAuditQuery.isError,
            }}
          />
        ) : null}

        <Outlet />
      </div>
    </AppShell>
  );
};

export default Audit;
